import React from 'react';

interface BusinessReportRTFProps {
  title: string;
  content: string;
  companyName?: string;
}

/**
 * Component for generating RTF business reports compatible with Word and other text editors
 */
export const BusinessReportRTF: React.FC<BusinessReportRTFProps> = ({ 
  title, 
  content, 
  companyName = 'Su Empresa' 
}) => {
  // Convert text to RTF format
  const convertToRTF = (plainText: string): string => {
    if (!plainText) return '';
    
    // Separate the text into lines
    const lines = plainText.split(/\r?\n/).filter(line => line.trim() !== '');
    
    // Use the provided title or the first line as title
    const documentTitle = title || (lines.length > 0 ? lines[0] : 'Documento Empresarial');
    const contentLines = title ? lines : (lines.length > 1 ? lines.slice(1) : []);
    
    // Escape RTF special characters
    const escapeRTF = (text: string) => {
      return text
        .replace(/\\/g, '\\\\')
        .replace(/\{/g, '\\{')
        .replace(/\}/g, '\\}')
        .replace(/á/g, '\\'+'\'e1')
        .replace(/é/g, '\\'+'\'e9')
        .replace(/í/g, '\\'+'\'ed')
        .replace(/ó/g, '\\'+'\'f3')
        .replace(/ú/g, '\\'+'\'fa')
        .replace(/ñ/g, '\\'+'\'f1')
        .replace(/Á/g, '\\'+'\'c1')
        .replace(/É/g, '\\'+'\'c9')
        .replace(/Í/g, '\\'+'\'cd')
        .replace(/Ó/g, '\\'+'\'d3')
        .replace(/Ú/g, '\\'+'\'da')
        .replace(/Ñ/g, '\\'+'\'d1');
    };
    
    // Standard RTF header
    let rtf = '{\\rtf1\\ansi\\ansicpg1252\\deff0\\nouicompat\\deflang3082{\\fonttbl{\\f0\\fnil\\fcharset0 Calibri;}}';
    rtf += '{\\colortbl ;\\red44\\green85\\blue123;\\red34\\green34\\blue34;}';
    rtf += '{\\*\\generator Business Report Generator;}';
    rtf += '\\viewkind4\\uc1';
    rtf += '\\pard\\sa200\\sl276\\slmult1\\qc\\cf1\\f0\\fs28\\b ' + escapeRTF(documentTitle) + '\\cf0\\b0\\par\n';
    
    // Current date formatted
    const today = new Date();
    const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    const dateStr = today.toLocaleDateString('es-ES', dateOptions);
    
    rtf += '\\pard\\sa200\\sl276\\slmult1\\qr\\fs20\\i ' + escapeRTF(dateStr) + '\\i0\\par\n';
    
    // Process each line to detect formats
    contentLines.forEach(line => {
      const trimmedLine = line.trim();
      
      // Detect if it's a possible subtitle (short line without a final period)
      if (trimmedLine.length < 50 && !trimmedLine.endsWith('.') && !trimmedLine.endsWith('?') && !trimmedLine.endsWith('!')) {
        rtf += '\\pard\\sa200\\sl276\\slmult1\\fs24\\b\\cf1 ' + escapeRTF(trimmedLine) + '\\cf0\\b0\\par\n';
      } 
      // Detect if it's a list item
      else if (trimmedLine.match(/^[\d\-\*\•][\.\)\-\s]/)) {
        rtf += '\\pard\\fi-360\\li720\\sa200\\sl276\\slmult1\\fs22 • ' + 
               escapeRTF(trimmedLine.replace(/^[\d\-\*\•][\.\)\-\s]/, '')) + '\\par\n';
      }
      // Normal paragraph 
      else {
        rtf += '\\pard\\sa200\\sl276\\slmult1\\qj\\fs22\\cf2 ' + escapeRTF(trimmedLine) + '\\cf0\\par\n';
      }
    });
    
    // Footer
    rtf += '\\pard\\sa200\\sl276\\slmult1\\qc\\fs18\\i Documento generado automáticamente\\par\n';
    rtf += '© ' + today.getFullYear() + ' ' + escapeRTF(companyName) + '\\i0\\par\n';
    
    // RTF closing
    rtf += '}';
    
    return rtf;
  };

  const downloadRTF = () => {
    try {
      const rtfContent = convertToRTF(content);
      const blob = new Blob([rtfContent], { type: 'application/rtf' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      a.download = `informe-${timestamp}.rtf`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading RTF report:', error);
      alert('Error al descargar el informe RTF');
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold mb-3 text-green-700">Informe RTF Empresarial</h3>
      <p className="text-sm text-gray-600 mb-4">
        Genera un documento RTF compatible con Microsoft Word y otros editores de texto, 
        con formato profesional y estructura empresarial.
      </p>
      
      <div className="flex flex-col space-y-3">
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Título:</span>
          <span className="text-sm text-gray-600">{title || 'Documento Empresarial'}</span>
        </div>
        
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Empresa:</span>
          <span className="text-sm text-gray-600">{companyName}</span>
        </div>
        
        <div className="flex items-center">
          <span className="text-sm font-medium text-gray-700 w-24">Contenido:</span>
          <span className="text-sm text-gray-600 truncate">{content.substring(0, 50)}...</span>
        </div>
      </div>
      
      <button
        onClick={downloadRTF}
        className="mt-4 w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center"
      >
        Descargar Informe RTF
      </button>
    </div>
  );
};